import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import _ from 'lodash';
import MarkdownIt from 'markdown-it';

// MUI imports
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListSubheader from '@mui/material/ListSubheader';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import { grey, blue, green, orange } from '@mui/material/colors';

// The rule book renders from the same files the agents load (docs/air-agent), served by
// /api/admin/rate-agent/*. Raw HTML inside a rule file is shown as text, not run.
const md = new MarkdownIt({ html: false, linkify: true });

const GROUPS = [
    { key: 'rules', label: 'Rules (edit these)' },
    { key: 'packs', label: 'Agent packs (generated)' },
    { key: 'design', label: 'Design' },
    { key: 'cases', label: 'Cases' },
];

function docTitle(name) {
    return name.replace(/^cases\//, '').replace(/\.md$/, '');
}

const MARKDOWN_SX = {
    fontSize: '14px', lineHeight: 1.6, color: grey[900],
    '& h1': { fontSize: '24px', mt: 0 }, '& h2': { fontSize: '19px', mt: 4, pb: '4px', borderBottom: `1px solid ${grey[200]}` }, '& h3': { fontSize: '16px', mt: 3 },
    '& p:first-of-type': { mt: 0 },
    '& code': { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '12.5px', background: grey[100], px: '4px', borderRadius: '3px' },
    '& pre': { background: grey[50], border: `1px solid ${grey[200]}`, borderRadius: '6px', p: 1.5, overflowX: 'auto' },
    '& pre code': { background: 'none', px: 0 },
    '& table': { borderCollapse: 'collapse', display: 'block', overflowX: 'auto', my: 2 },
    '& th, & td': { border: `1px solid ${grey[300]}`, px: 1, py: '4px', textAlign: 'left', verticalAlign: 'top' },
    '& th': { background: grey[50] },
    '& blockquote': { borderLeft: `3px solid ${blue[200]}`, m: 0, pl: 2, color: grey[700] },
    '& a': { color: blue[700] },
    '& p.rule-from': { mt: '-6px', mb: 1.5, fontSize: '12.5px', color: grey[600], fontStyle: 'italic' },
};

function VersionBox({ source, compact }) {
    const [showHistory, setShowHistory] = useState(false);
    if (!source) return null;
    return (
        <Box sx={{ border: `1px solid ${grey[200]}`, background: grey[50], borderRadius: '6px', p: compact ? 1 : 1.5, display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px' }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', alignItems: 'baseline' }}>
                <span><b>Latest version:</b> {source.version ? dayjs(source.version.date).format('MMM D, YYYY') : 'not committed yet'}
                    {source.version && <Box component='span' sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '12px', color: grey[600], ml: 1 }}>{source.version.commit}</Box>}
                </span>
                <span style={{ color: grey[700] }}>{source.rules} rules</span>
                {source.history.length > 0 && (
                    <Box component='button' type='button' onClick={() => setShowHistory(v => !v)}
                        sx={{ border: 0, background: 'none', p: 0, font: 'inherit', color: blue[700], cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                        {showHistory ? 'Hide' : 'Show'} {source.history.length} {source.history.length === 1 ? 'change' : 'changes'}
                    </Box>
                )}
            </Box>
            {source.basedOn && <span><b>Based on:</b> {source.basedOn}</span>}
            {showHistory && (
                <Box component='ul' sx={{ m: 0, mt: '4px', pl: 2.5, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    {source.history.map(h => (
                        <li key={h.commit}>
                            <Box component='span' sx={{ color: grey[700] }}>{dayjs(h.date).format('MMM D, YYYY')}</Box>
                            {' · '}{h.subject}
                            <Box component='span' sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '11px', color: grey[500], ml: 1 }}>{h.commit}</Box>
                        </li>
                    ))}
                </Box>
            )}
        </Box>
    );
}

// Under each rule heading, say which case or data it came from ("From: ..."), so the
// source of a rule is visible without scrolling to the end of it.
function annotateRuleSources(html, rules) {
    if (!rules?.length) return html;
    return html.replace(/<h2>([A-Z]+\d+[a-z]?) — ([\s\S]*?)<\/h2>/g, (match, id) => {
        const rule = rules.find(r => r.id === id);
        if (!rule?.from) return match;
        return `${match}<p class="rule-from">Based on: ${md.renderInline(rule.from)}</p>`;
    });
}

export function RateAgentRules({ accesstoken }) {
    const [docs, setDocs] = useState(null);
    const [error, setError] = useState(null);
    const [selected, setSelected] = useState(() => new URLSearchParams(window.location.search).get('doc') || 'rules-air.md');
    const [doc, setDoc] = useState(null);
    const [docError, setDocError] = useState(null);
    const [routing, setRouting] = useState(null);

    useEffect(() => {
        axios.get(`/api/admin/rate-agent/routing?accesstoken=${accesstoken}`)
            .then(({ data }) => setRouting(data))
            .catch(() => setRouting(null));
    }, [accesstoken]);

    useEffect(() => {
        axios.get(`/api/admin/rate-agent/docs?accesstoken=${accesstoken}`)
            .then(({ data }) => setDocs(data.docs))
            .catch(e => setError(e.response?.data?.detail || e.message));
    }, [accesstoken]);

    useEffect(() => {
        if (!selected) return;
        setDoc(null);
        setDocError(null);
        axios.get(`/api/admin/rate-agent/doc?name=${encodeURIComponent(selected)}&accesstoken=${accesstoken}`)
            .then(({ data }) => setDoc(data))
            .catch(e => setDocError(e.response?.data?.detail || e.message));
        const url = new URL(window.location.href);
        url.searchParams.set('doc', selected);
        window.history.replaceState({}, '', url);
    }, [selected, accesstoken]);

    const source = routing?.sources?.[selected];
    const sourceRules = useMemo(() => {
        if (!routing || !source) return [];
        const all = [...routing.classification.rules, ..._.flatMap(Object.values(routing.packs), p => p.rules)];
        return _.uniqBy(all.filter(r => r.source === selected), 'id');
    }, [routing, source, selected]);
    const html = useMemo(() => (doc ? annotateRuleSources(md.render(doc.markdown), sourceRules) : ''), [doc, sourceRules]);
    const selectedMeta = docs?.find(d => d.name === selected);

    if (error) return <Alert severity='error'>{error}</Alert>;
    if (!docs) return <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;

    return (
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
            <Box sx={{ width: 260, flexShrink: 0, border: `1px solid ${grey[200]}`, borderRadius: '8px', maxHeight: 'calc(100vh - 200px)', overflowY: 'auto', position: 'sticky', top: 16 }}>
                <List dense disablePadding>
                    {GROUPS.map(g => {
                        const items = docs.filter(d => d.group === g.key);
                        if (!items.length) return null;
                        return (
                            <React.Fragment key={g.key}>
                                <ListSubheader sx={{ lineHeight: '32px', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: grey[600], background: grey[50] }}>
                                    {g.label}
                                </ListSubheader>
                                {items.map(d => (
                                    <ListItemButton key={d.name} selected={d.name === selected} onClick={() => setSelected(d.name)} sx={{ py: '3px' }}>
                                        <Typography sx={{ fontSize: '13px', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>{docTitle(d.name)}</Typography>
                                    </ListItemButton>
                                ))}
                            </React.Fragment>
                        );
                    })}
                </List>
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
                {selectedMeta?.group === 'packs' && (
                    <Alert severity='info' sx={{ mb: 2 }}>
                        This pack is generated by <code>build-corpus.py</code> from the rules files. Edit the <b>rules-*.md</b> file instead; changes made here are overwritten on the next build.
                    </Alert>
                )}
                {selectedMeta && (
                    <Typography variant='caption' sx={{ color: grey[600], display: 'block', mb: 1 }}>
                        docs/air-agent/{selectedMeta.name} · {Math.round(selectedMeta.size / 1024)} KB · updated {dayjs(selectedMeta.modified).format('MMM D, YYYY')}
                    </Typography>
                )}
                {source && <Box sx={{ mb: 2, maxWidth: '860px' }}><VersionBox source={source} /></Box>}
                {docError && <Alert severity='error'>{docError}</Alert>}
                {!doc && !docError && <CircularProgress size={24} />}
                {doc && (
                    <Box sx={{ ...MARKDOWN_SX, maxWidth: '860px' }} dangerouslySetInnerHTML={{ __html: html }} />
                )}
            </Box>
        </Box>
    );
}

const ROUTE = {
    parser: { label: 'Parser', bg: green[50], color: green[800] },
    cargofive: { label: 'CargoFive', bg: blue[50], color: blue[800] },
    'cargofive-or-parser': { label: 'CargoFive, or parser if tiered', bg: orange[50], color: orange[900] },
};

const MODE_GROUPS = [
    { label: 'Air', ids: ['air-freight', 'air-drayage'] },
    { label: 'Ocean', ids: ['ocean-lcl-freight', 'ocean-lcl-drayage', 'ocean-fcl-freight', 'ocean-fcl-drayage'] },
    { label: 'Road', ids: ['land-ftl', 'land-ltl'] },
];

const panelSx = { border: `1px solid ${grey[200]}`, borderRadius: '8px', p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0, background: '#fff' };
const stepSx = { fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: blue[700] };
const monoSx = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '12px' };

function RuleRow({ rule, onOpen }) {
    return (
        <Box component='button' type='button' onClick={() => onOpen(rule)}
            sx={{ display: 'flex', gap: 1, alignItems: 'baseline', textAlign: 'left', width: '100%', border: 0, background: 'none', p: '4px 6px', borderRadius: '4px', cursor: 'pointer', font: 'inherit', color: grey[900], '&:hover': { background: blue[50] }, '&:focus-visible': { outline: `2px solid ${blue[400]}` } }}>
            <Typography component='span' sx={{ ...monoSx, fontWeight: 600, color: blue[800], minWidth: '34px', flexShrink: 0 }}>{rule.id}</Typography>
            <Typography component='span' sx={{ fontSize: '13px', lineHeight: 1.4, '& code': { ...monoSx, background: grey[100], px: '3px', borderRadius: '3px' } }} dangerouslySetInnerHTML={{ __html: md.renderInline(rule.title) }} />
        </Box>
    );
}

function RateAgentRouting({ accesstoken }) {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [selected, setSelected] = useState(() => new URLSearchParams(window.location.search).get('category') || 'air-freight');
    const [openRule, setOpenRule] = useState(null);

    useEffect(() => {
        axios.get(`/api/admin/rate-agent/routing?accesstoken=${accesstoken}`)
            .then(({ data }) => setData(data))
            .catch(e => setError(e.response?.data?.detail || e.message));
    }, [accesstoken]);

    function choose(id) {
        setSelected(id);
        const url = new URL(window.location.href);
        url.searchParams.set('category', id);
        window.history.replaceState({}, '', url);
    }

    if (error) return <Alert severity='error'>{error}</Alert>;
    if (!data) return <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;

    const category = data.categories.find(c => c.id === selected) || data.categories[0];
    const pack = data.packs[category.pack];
    const bySource = pack.parts.map(part => ({ part, rules: pack.rules.filter(r => r.source === part) }));
    const classificationRule = id => data.classification.rules.find(r => r.id === id);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant='body2' sx={{ color: grey[700], maxWidth: '90ch' }}>
                Pick what the classification agent decided to see the rules the parser gets for it. Click any rule to read it in full. This view is built from the same files the agents load (routing.json, generated {data.generated}).
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0,1fr) minmax(0,1fr) minmax(0,1.5fr)' }, gap: 2, alignItems: 'start' }}>
                <Box sx={panelSx}>
                    <Typography sx={stepSx}>1 · Classification agent</Typography>
                    <Typography sx={{ fontSize: '13px', color: grey[700] }}>
                        Loads <Box component='span' sx={monoSx}>{data.classification.file}</Box>, {data.classification.rules.length} rules, and decides what kind of file it is.
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        {data.classification.rules.map(r => <RuleRow key={r.id} rule={r} onOpen={setOpenRule} />)}
                    </Box>
                </Box>

                <Box sx={panelSx}>
                    <Typography sx={stepSx}>2 · What it decided</Typography>
                    {MODE_GROUPS.map(g => (
                        <Box key={g.label} sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <Typography sx={{ fontSize: '12px', fontWeight: 700, color: grey[600] }}>{g.label}</Typography>
                            {g.ids.map(id => {
                                const c = data.categories.find(x => x.id === id);
                                if (!c) return null;
                                const active = c.id === category.id;
                                return (
                                    <Box key={c.id} component='button' type='button' onClick={() => choose(c.id)}
                                        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, border: `1px solid ${active ? blue[400] : grey[200]}`, background: active ? blue[50] : '#fff', borderRadius: '6px', p: '6px 10px', cursor: 'pointer', font: 'inherit', textAlign: 'left', '&:hover': { borderColor: blue[300] }, '&:focus-visible': { outline: `2px solid ${blue[400]}` } }}>
                                        <Typography component='span' sx={{ fontSize: '13px', fontWeight: active ? 700 : 500 }}>{c.label}</Typography>
                                        <Chip size='small' label={ROUTE[c.route].label} sx={{ height: '20px', fontSize: '11px', background: ROUTE[c.route].bg, color: ROUTE[c.route].color }} />
                                    </Box>
                                );
                            })}
                        </Box>
                    ))}
                    <Box sx={{ borderTop: `1px solid ${grey[200]}`, pt: 1.5, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <Typography sx={{ fontSize: '12px', fontWeight: 700, color: grey[600] }}>How it got to <Box component='span' sx={monoSx}>{category.id}</Box></Typography>
                        {category.path.map((step, i) => (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', fontSize: '13px' }}>
                                <span>{step.question}</span>
                                <span>→</span>
                                <b>{step.answer}</b>
                                <Chip size='small' label={step.rule} onClick={() => setOpenRule(classificationRule(step.rule))} sx={{ height: '20px', fontSize: '11px', ...monoSx, cursor: 'pointer' }} />
                            </Box>
                        ))}
                        <Typography sx={{ fontSize: '13px', color: grey[700] }}>{category.summary}</Typography>
                    </Box>
                </Box>

                <Box sx={panelSx}>
                    <Typography sx={stepSx}>3 · Pack the parser gets</Typography>
                    <Box>
                        <Typography sx={{ fontSize: '16px', fontWeight: 700 }}>{pack.title}</Typography>
                        <Typography sx={{ ...monoSx, color: grey[600] }}>{pack.file} · {pack.rules.length} rules</Typography>
                    </Box>
                    {category.route !== 'parser' && (
                        <Alert severity='info' sx={{ py: 0, fontSize: '13px' }}>
                            {category.route === 'cargofive'
                                ? 'Today this file goes to CargoFive, not the parser (C8). The parser would load this pack only in a shadow run.'
                                : 'Ordinary LCL goes to CargoFive (C8). Only tiered LCL, priced in W/M bands, comes to the parser with this pack.'}
                        </Alert>
                    )}
                    <Box sx={{ fontSize: '13px', color: grey[800], '& p': { m: 0 }, '& code': { ...monoSx, background: grey[100], px: '3px', borderRadius: '3px' } }} dangerouslySetInnerHTML={{ __html: md.render(pack.blurb) }} />
                    {bySource.map(({ part, rules }) => (
                        <Box key={part} sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <Typography sx={{ ...monoSx, fontWeight: 600, color: grey[700], borderBottom: `1px solid ${grey[200]}`, pb: '4px' }}>
                                {part} · {rules.length} {rules.length === 1 ? 'rule' : 'rules'}{part === 'rules-shared.md' ? ' · in every pack' : ''}
                            </Typography>
                            <VersionBox source={data.sources?.[part]} compact />
                            {rules.map(r => <RuleRow key={r.id} rule={r} onOpen={setOpenRule} />)}
                        </Box>
                    ))}
                </Box>
            </Box>

            {openRule && (
                <Dialog open={true} onClose={() => setOpenRule(null)} maxWidth='md' fullWidth>
                    <DialogTitle sx={{ pb: 0 }}>
                        <Box component='span' sx={{ ...monoSx, fontSize: '15px', color: blue[800], mr: 1 }}>{openRule.id}</Box><span dangerouslySetInnerHTML={{ __html: md.renderInline(openRule.title) }} />
                        <Typography sx={{ ...monoSx, color: grey[600], mt: '4px' }}>{openRule.source}</Typography>
                        {openRule.from && (
                            <Typography sx={{ fontSize: '13px', color: grey[700], mt: '4px', fontStyle: 'italic', '& code': { ...monoSx } }}>
                                Based on: <span dangerouslySetInnerHTML={{ __html: md.renderInline(openRule.from) }} />
                            </Typography>
                        )}
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ ...MARKDOWN_SX, pt: 2 }} dangerouslySetInnerHTML={{ __html: md.render(openRule.body) }} />
                    </DialogContent>
                    <DialogActions><Button onClick={() => setOpenRule(null)}>Close</Button></DialogActions>
                </Dialog>
            )}
        </Box>
    );
}

// How the agents get better. Nothing is learned automatically: the imports are evidence,
// a person turns evidence into rules, and the agents only ever read rules.
const LEARNING_STEPS = [
    { title: 'Evidence collects in Imports', body: 'Every uploaded file, what CargoFive or a person turned it into, the rates that came out, and your notes. Each row is an approved answer to "this file should become these rates".', status: 'live' },
    { title: 'The parser and users add to it', body: 'The parser records a gap wherever the rules ran out. On the review screen, every change a user makes is recorded as a correction, including a charge moved to another form section (Origin, Freight, Destination), which tells us where the charge glossary is wrong.', status: 'with-parser' },
    { title: 'You review the queue', body: 'Filter Imports to "Anything open". For each gap or correction, decide: the rules need updating, or no rule is needed. Record which rules file changed and why.', status: 'live' },
    { title: 'A rule is written and the packs rebuilt', body: 'Edit the narrowest rules file it belongs in, cite the import it came from, and run build-corpus.py. The file gets a new version, and the Rules tab shows it.', status: 'live' },
    { title: 'The next parse follows the new rule', body: 'The agents load the rebuilt packs. Nothing else changes how they behave: the model is never retrained, and nothing said to the parser in a run is kept.', status: 'live' },
];

const LEARNING_NEXT = [
    { title: 'Replay test', body: 'Run the parser on past imports that already have an approved result, chosen by date range and workspace, and score how close it gets. Check every rule change against real files before it ships.' },
    { title: 'Worked examples', body: 'When a file comes from a supplier seen before, give the parser that supplier\'s last approved import alongside the rules.' },
    { title: 'Draft a rule', body: 'On an import with open gaps or notes, ask Claude to draft the rule, pick the pack and cite the case. You approve or edit it, and the packs rebuild.' },
];

const STATUS_CHIP = {
    live: { label: 'Works today', bg: green[50], color: green[800] },
    'with-parser': { label: 'Starts with the parser', bg: orange[50], color: orange[900] },
    next: { label: 'Planned, needs the parser', bg: grey[100], color: grey[700] },
};

function RateAgentLearning() {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant='body2' sx={{ color: grey[700], maxWidth: '90ch' }}>
                The agents don't learn from the imports by themselves. The imports are evidence; a person turns that evidence into rules, and the agents only ever read rules. That keeps every change visible, tied to a real file, and reversible.
            </Typography>
            <Box component='ol' sx={{ m: 0, p: 0, listStyle: 'none', display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(5, minmax(0,1fr))' }, gap: 1.5 }}>
                {LEARNING_STEPS.map((step, i) => (
                    <Box component='li' key={step.title} sx={{ ...panelSx, gap: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ ...monoSx, fontWeight: 700, color: blue[700] }}>{i + 1}</Typography>
                            <Chip size='small' label={STATUS_CHIP[step.status].label} sx={{ height: '20px', fontSize: '11px', background: STATUS_CHIP[step.status].bg, color: STATUS_CHIP[step.status].color }} />
                        </Box>
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, lineHeight: 1.3 }}>{step.title}</Typography>
                        <Typography sx={{ fontSize: '13px', color: grey[700] }}>{step.body}</Typography>
                    </Box>
                ))}
            </Box>
            <Typography sx={{ fontSize: '12px', color: grey[600] }}>Step 5 feeds step 1: the next file is parsed with the new rule, and its result joins the evidence.</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography sx={{ fontSize: '14px', fontWeight: 700 }}>Making the imports teach it faster</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0,1fr))' }, gap: 1.5 }}>
                    {LEARNING_NEXT.map(item => (
                        <Box key={item.title} sx={{ ...panelSx, gap: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                                <Typography sx={{ fontSize: '14px', fontWeight: 700 }}>{item.title}</Typography>
                                <Chip size='small' label={STATUS_CHIP.next.label} sx={{ height: '20px', fontSize: '11px', background: STATUS_CHIP.next.bg, color: STATUS_CHIP.next.color }} />
                            </Box>
                            <Typography sx={{ fontSize: '13px', color: grey[700] }}>{item.body}</Typography>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
}

export function RateAgentArchitecture({ accesstoken }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>The whole system</Typography>
                <RateAgentArchitectureMap accesstoken={accesstoken} />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>How it learns</Typography>
                <RateAgentLearning />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>How a file is routed</Typography>
                <RateAgentRouting accesstoken={accesstoken} />
            </Box>
        </Box>
    );
}

function RateAgentArchitectureMap({ accesstoken }) {
    const [html, setHtml] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        axios.get(`/api/admin/rate-agent/architecture?accesstoken=${accesstoken}`)
            .then(({ data }) => setHtml(data.html))
            .catch(e => setError(e.response?.data?.detail || e.message));
    }, [accesstoken]);

    if (error) return <Alert severity='error'>{error}</Alert>;
    if (!html) return <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;

    // Shown in a sandboxed frame: the page brings its own styles and fonts, and runs no script.
    return (
        <Box component='iframe' title='The architecture' srcDoc={`<!doctype html><html data-theme="light"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"></head><body>${html}</body></html>`}
            sandbox='' sx={{ width: '100%', height: 'calc(100vh - 170px)', minHeight: '700px', border: `1px solid ${grey[200]}`, borderRadius: '8px', background: '#f5f6fa' }} />
    );
}

export const RATE_AGENT_VIEWS = [
    { key: 'imports', label: 'Imports' },
    { key: 'rules', label: 'Rules' },
    { key: 'architecture', label: 'The architecture' },
    { key: 'routing', label: 'Ocean routing' },
];
